import { PROJECT_NOTES_ATTRIBUTE } from "../components/EstimateContract/components/Estimating/estimatingDefaults";
import {
    buildGutterScope,
    buildNailingVentilationScope,
    formatPitchDisplay,
    formatTearOffLayerDisplay,
    getAttrDisplay,
    getAttributeNumericValue,
    getAttrObj,
} from "./contractScopeFormatters";
import {
    applyEstimatingToContractPrice,
    buildPricingBreakdownLines,
    calculateCartBreakdown,
    formatCurrency,
    parseEstimatingAttributes,
} from "./contractEstimating";

function normalizeAttributesList(raw: unknown): any[] {
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === "object" && Array.isArray((raw as any).Data)) {
        return (raw as any).Data;
    }
    return [];
}

function getMeasurement(attributes: any[], name: string): number {
    const val = getAttributeNumericValue(getAttrObj(attributes, name));
    return val !== null && val > 0 ? val : 0;
}

function getUnderlayment(attributes: any[], cartItems: any[]): string {
    const underlaymentType = getAttrDisplay(attributes, "Underlayment Type");
    if (underlaymentType !== "N/A") return underlaymentType;

    if (cartItems.some((item) => item.ProductCategory === "Field Dry In Synthetic")) {
        return "Synthetic underlayment";
    }
    if (cartItems.some((item) => item.ProductCategory === "Field Dry In 15 Felt")) {
        return "15 lb. felt";
    }
    return "Per scope";
}

function buildProjectNotes(attributes: any[]): string {
    const notes = getAttrDisplay(attributes, PROJECT_NOTES_ATTRIBUTE);
    return notes !== "N/A" ? notes : "No special notes for this project.";
}

export interface BuildContractPreviewArgs {
    project: any;
    attributes: any[];
    cartItems: any[];
    estimatedStartDate?: string;
}

export function buildContractPreviewData({
    project,
    attributes,
    cartItems,
    estimatedStartDate,
}: BuildContractPreviewArgs) {
    const safeAttributes = normalizeAttributesList(attributes);
    const safeCartItems = Array.isArray(cartItems) ? cartItems : [];
    const areaSq = getMeasurement(safeAttributes, "Area") / 100;
    const est = parseEstimatingAttributes(safeAttributes);
    const breakdown = calculateCartBreakdown(safeCartItems);
    const pricing = applyEstimatingToContractPrice({
        est,
        ...breakdown,
    });

    const roofMaterial =
        safeCartItems.find((i: any) => i.ProductCategory === "Field Shingles")?.ProductName || "N/A";
    const dateStr = new Date().toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    });
    const startDate =
        estimatedStartDate ||
        (project.StartDate
            ? new Date(project.StartDate).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
              })
            : "TBD");

    return {
        Header: {
            ReferenceNumber: String(project.Id || "0000").padStart(6, "0"),
            Date: dateStr,
            Status: "Preview",
        },
        OwnerInfo: {
            Name: project.CustomerName || "N/A",
            SalesRep: project.SalesmanName || "N/A",
            Date: dateStr,
            Address: project.CustomerStreet || "N/A",
            City: project.CustomerCity || "N/A",
            State: project.CustomerState || "N/A",
            Zip: `${project.CustomerZipCode || ""}${project.CustomerCountry ? ` · ${project.CustomerCountry}` : ""}`,
            Phone: project.CustomerPhoneNumber || "N/A",
            Email: project.CustomerEmail || "N/A",
            InsuranceCompany: project.InsuranceProvider || "N/A",
            ClaimNumber: project.InsuranceClaimNumber || "N/A",
        },
        RoofingScope: {
            Material: roofMaterial,
            Color: getAttrDisplay(safeAttributes, "Roof Color"),
            Pitch: formatPitchDisplay(safeAttributes),
            Layers: formatTearOffLayerDisplay(safeAttributes),
            Squares: `${areaSq} SQ`,
            Warranty: "30 Years",
            Underlayment: getUnderlayment(safeAttributes, safeCartItems),
            IceWaterShield: "Eaves & Valleys",
            StarterStrip: "ALL",
            EaveDripEdge: getAttrDisplay(safeAttributes, "Eave Drip Edge"),
            RakeDripEdge: getAttrDisplay(safeAttributes, "Rake Drip Edge"),
            ValleyType: getAttrDisplay(safeAttributes, "Valley Type"),
            HipRidgeCap: "Factory Match",
        },
        NailingVentilation: {
            FieldNailSize: '1 1/4"',
            FieldNailsPerShingle: "6 Nails",
            RidgeNailSize: '1 1/2"',
            RidgeNailsPerCap: "4 Nails",
            ...buildNailingVentilationScope(safeAttributes),
        },
        Notes: buildProjectNotes(safeAttributes),
        GutterScope: buildGutterScope(safeAttributes),
        Pricing: {
            RoofPrice: formatCurrency(
                breakdown.roofMaterials +
                    breakdown.roofLabor +
                    pricing.wasteMarkup +
                    pricing.steepCharge +
                    pricing.extraLayerCharge,
            ),
            GutterPrice: formatCurrency(breakdown.gutterMaterials + breakdown.gutterLabor),
            TotalPrice: formatCurrency(pricing.sellingPrice),
            PaymentSchedule: pricing.paymentSchedule,
            EstimatedStartDate: startDate,
            UpgradeNotes: est.shingleUpgradeNotes,
            UpgradeAmount: est.shingleUpgradeAmount > 0 ? formatCurrency(est.shingleUpgradeAmount) : "",
            Breakdown: buildPricingBreakdownLines(breakdown, pricing, est, formatCurrency),
        },
    };
}
